import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Privacy Policy | Son's Guadalupe"
        description="Son's Guadalupe privacy policy. Learn how we collect, use, and protect your personal information when you visit our site or book with us."
        canonical="/privacy"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-teal pt-40 pb-20 md:pt-48 md:pb-24">
        <div className="container-narrow mx-auto px-4 md:px-8 text-center">
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-white/90 text-lg md:text-xl">
            Last Updated: January 2025
          </p>
        </div>
      </section>

      <main className="py-16">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="bg-white rounded-2xl shadow-elevated p-8 md:p-12 -mt-12 relative z-10">
            <div className="prose prose-lg max-w-none text-charcoal/80">
              <p className="mb-8">
                Son's Getaways, LLC ("Company," "we," "us," or "our"), doing business as Son's Guadalupe, is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, make a reservation, or stay at our property located in New Braunfels, Texas.
              </p>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  1. Information We Collect
                </h2>
                
                <h3 className="font-heading text-xl font-semibold text-teal mb-3">
                  Personal Information
                </h3>
                <p className="mb-4">
                  When you make a reservation or contact us, we may collect:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-teal">
                  <li>Name and contact information (email address, phone number, mailing address)</li>
                  <li>Payment information (credit card details, billing address)</li>
                  <li>Reservation details (dates of stay, number of guests, cabin preferences)</li>
                  <li>Government-issued ID (for verification purposes)</li>
                  <li>Vehicle information (for parking and property access)</li>
                  <li>Names and ages of minors in your party (for waiver purposes)</li>
                </ul>

                <h3 className="font-heading text-xl font-semibold text-teal mb-3">
                  Automatically Collected Information
                </h3>
                <p className="mb-4">
                  When you visit our website, we may automatically collect:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4 marker:text-teal">
                  <li>IP address and browser type</li>
                  <li>Device information and operating system</li>
                  <li>Pages visited and time spent on our website</li>
                  <li>Referring website or source</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  2. How We Use Your Information
                </h2>
                <p className="mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4 marker:text-teal">
                  <li>Process and manage your cabin reservations</li>
                  <li>Communicate with you about your booking, including confirmations and reminders</li>
                  <li>Process payments and prevent fraud</li>
                  <li>Provide customer support and respond to inquiries</li>
                  <li>Send promotional emails about specials and updates (with your consent)</li>
                  <li>Improve our website and services</li>
                  <li>Comply with legal obligations and protect our rights</li>
                  <li>Ensure the safety and security of our guests and property</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  3. Information Sharing and Disclosure
                </h2>
                <p className="mb-4">
                  We do not sell your personal information. We may share your information with:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4 marker:text-teal">
                  <li><strong>Service Providers:</strong> Third-party vendors who assist with payment processing, email communications, website hosting, and reservation management</li>
                  <li><strong>Affiliated Properties:</strong> Other Son's Getaways properties (Son's Island, Son's Blue River Camp, Son's River Ranch) for cross-promotional purposes</li>
                  <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                  <li><strong>Safety and Security:</strong> To protect the rights, property, or safety of our guests, employees, or the public</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  4. Cookies and Tracking Technologies
                </h2>
                <p className="mb-4">
                  Our website uses cookies and similar technologies to enhance your browsing experience, analyze website traffic, and understand where our visitors come from. You can control cookie preferences through your browser settings. Disabling cookies may limit some functionality of our website.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  5. Data Security
                </h2>
                <p className="mb-4">
                  We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Payment information is processed through secure, PCI-compliant payment processors. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  6. Data Retention
                </h2>
                <p className="mb-4">
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements. Reservation records are typically retained for seven (7) years for tax and legal purposes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  7. Your Rights and Choices
                </h2>
                <p className="mb-4">
                  You have the right to:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4 marker:text-teal">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your information (subject to legal retention requirements)</li>
                  <li>Opt out of marketing communications at any time</li>
                  <li>Withdraw consent where processing is based on consent</li>
                </ul>
                <p className="text-charcoal/60">
                  To exercise these rights, please contact us using the information provided below.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  8. Third-Party Links
                </h2>
                <p className="mb-4">
                  Our website may contain links to third-party websites or services. We are not responsible for the privacy practices of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  9. Children's Privacy
                </h2>
                <p className="mb-4">
                  Our website is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately so we can delete such information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  10. Photography and Media
                </h2>
                <p className="mb-4">
                  By visiting Son's Guadalupe, you acknowledge that photographs or videos may be taken on the property for marketing and promotional purposes. If you do not wish to appear in any media, please inform our staff upon arrival. As stated in our liability waiver, guests grant permission for the use of images or video in marketing activities.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  11. Changes to This Privacy Policy
                </h2>
                <p className="mb-4">
                  We may update this Privacy Policy from time to time. The updated version will be indicated by the "Last Updated" date at the top of this page. We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  12. Contact Us
                </h2>
                <p className="mb-4">
                  If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-cream/50 rounded-xl p-6 mb-6">
                  <p className="font-heading font-bold text-teal text-lg mb-2">Son's Guadalupe</p>
                  <p className="mb-4">Son's Getaways, LLC</p>
                  <ul className="list-none space-y-1">
                    <li>7308 River Road</li>
                    <li>New Braunfels, Texas 78132</li>
                    <li className="mt-3"><strong>Phone:</strong> 877-577-7667</li>
                    <li><strong>Email:</strong> info@sonsgetaways.com</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-teal mb-4">
                  Texas Residents
                </h2>
                <p className="mb-4">
                  Under the Texas Data Privacy and Security Act (TDPSA), Texas residents may have additional rights regarding their personal data. To exercise your rights or for more information, please contact us using the information above.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
